import { useState, useMemo, useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router";
import axios from "axios";
import InnoIcons from "@InnoIcons/";
import SC from "./style";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import OccupantFilterDropdown from "components/OccupantFilterDropdown";
import { AccessDeniedMessage, Button } from "@InnoUI/";
import { convertKB, readFromLocalStorage, showAlert } from "utils/helper";
import theme from "@theme/";
import { apiPost } from "api/callApi";
import { useSearchParams } from "react-router-dom";
import ShimmerCard from "views/Dashboard/ShimmerCard";
import { GeneratingText } from "components/GeneratingText";
import { CircularProgress } from "@mui/material";
import DeleteComponent from "views/AdminSettings/DeleteComponent";
import { TextInput } from "@InnoInput/";
import { projectType, userAccessType } from "utils/lookUpTable";
import { set } from "date-fns";

function updateFileById(data, fileId, updates) {
  return {
    ...data,
    files: data.files.map((file) =>
      file.id === fileId ? { ...file, ...updates } : file,
    ),
  };
}

function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Memoized so toggling one file's checkbox doesn't force every other card's
// TextInput (and thumbnail) in the grid to re-render — that full-grid
// re-render on every click was the source of the checkbox lag.
const FileCard = memo(function FileCard({
  file,
  selected,
  access,
  onToggleSelect,
  onOpen,
  onRenameFile,
}) {
  return (
    <SC.MediaCard
      selected={selected}
      onClick={(e) => {
        if (e.target.type === "checkbox") return;
        onOpen(file);
      }}
    >
      <SC.CheckboxWrapper onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(file.id)}
          disabled={!access}
        />
      </SC.CheckboxWrapper>

      <SC.Thumbnail>
        {file.media_type === "image" ? (
          <SC.ThumbnailImage src={file.publicUrl} alt={file.fileName} />
        ) : (
          <video style={{ width: "100%", height: "100%", objectFit: "cover" }}>
            <source src={file.publicUrl} />
          </video>
        )}
        <SC.MediaType>
          {file.media_type === "image" ? (
            <>
              <InnoIcons.ImageIcon sx={{ fontSize: "12px" }} />
              Image
            </>
          ) : (
            <>
              <InnoIcons.VideocamIcon sx={{ fontSize: "12px" }} />
              Video
            </>
          )}
        </SC.MediaType>
      </SC.Thumbnail>
      <SC.CardInfo>
        <SC.FileNameEditor
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TextInput
            defaultValue={file.fileName}
            variant="borderless"
            autoSize={{ minRows: 2, maxRows: 3 }}
            isTextArea
            onChange={(value) => onRenameFile(value, "fileName", file.id)}
            disabled={!access}
          />
        </SC.FileNameEditor>

        <SC.FileSize>({convertKB(file.fileSize)})</SC.FileSize>
      </SC.CardInfo>
    </SC.MediaCard>
  );
});

// axios error responses keep the requested responseType, so a failed zip
// request comes back as a Blob holding the JSON error body, not JSON itself.
async function extractZipErrorMessage(error) {
  const errorBlob = error?.response?.data;
  if (errorBlob instanceof Blob) {
    try {
      const parsed = JSON.parse(await errorBlob.text());
      if (parsed?.message) return parsed.message;
    } catch {
      // not JSON, fall through to generic message
    }
  }
  return error?.message || "Failed to download files";
}

const { REACT_APP_API_KEY: ACCESS_TOKEN } = process.env;
const BASE_URL = "/api";

const CatalogDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const catalogId = searchParams.get("catalogId");
  const projectId = searchParams.get("projectId");
  const folderId = searchParams.get("folderId");
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [access, setAccess] = useState(false);
  const accessLevel = parseInt(localStorage.getItem("accessLevel") || "0", 10);

  // download states
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [zipDownloadTarget, setZipDownloadTarget] = useState(null); // "selected" | "catalog" | null
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState({
    files: [],
    permission: 0,
  });

  console.log("datadata", data);
  const [loading, setLoading] = useState(true);
  const [isdelete, setIsDelete] = useState(false);
  const [isCatalogsDelete, setIsCatalogsDelete] = useState(false);
  const [openDeleteView, setOpenDeleteView] = useState(false);
  const [catalogs, setCatalogs] = useState(false);

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return data.files;

    const q = searchQuery.toLowerCase();
    return data.files.filter((f) => f.fileName.toLowerCase().includes(q));
  }, [data.files, searchQuery]);

  const handleDownload = async (file) => {
    // prevent accidental double-clicks
    if (downloading) return;

    setDownloading(true);
    setDownloadProgress(0);
    setDownloadingFileId(file.id);

    try {
      const response = await fetch(file.publicUrl, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        redirect: "follow",
      });

      if (!response.ok) {
        console.error(
          "HTTP error when fetching file:",
          response.status,
          response.statusText,
        );
        // fallback: open in new tab so user can try manually
        window.open(file.publicUrl, "_blank");
        return;
      }

      // If the browser and server support streaming, show progress
      const contentLengthHeader = response.headers.get("content-length");
      const total = contentLengthHeader
        ? parseInt(contentLengthHeader, 10)
        : null;
      const contentType =
        response.headers.get("content-type") || file.fileType || undefined;

      // Renaming a file doesn't require keeping the extension, but the
      // browser needs one (or a MIME type) to save it as the right file
      // type — use the extension the API resolved from the stored fileType
      // so the downloaded name always keeps the original ext.
      const ext = file.fileExtension || "";
      const baseName = file.fileName || "download";
      const downloadName =
        ext && !baseName.toLowerCase().endsWith(`.${ext.toLowerCase()}`)
          ? `${baseName}.${ext}`
          : baseName;

      if (response.body && total) {
        // streaming with progress
        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          receivedLength += value.length;
          const percent = Math.round((receivedLength / total) * 100);
          setDownloadProgress(percent);
        }

        const blob = new Blob(chunks, { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setDownloadProgress(100);
      } else {
        // fallback: simply get blob (no percent) or streaming not supported
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        // indicate complete (we couldn't show intermediate progress)
        setDownloadProgress(100);
      }
    } catch (error) {
      console.error("Download failed:", error);
      // fallback: open link in new tab
      window.open(file.publicUrl, "_blank");
    } finally {
      // small delay so UI shows 100% briefly, then reset
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
        setDownloadingFileId(null);
      }, 500);
    }
  };

  const downloadFilesAsZip = async (fileIds, zipName, target) => {
    if (!fileIds.length || zipDownloadTarget) return;

    setZipDownloadTarget(target);
    try {
      if (fileIds.length === 1) {
        const result = await apiPost("/v1/photos/downloadcatalogzip", {
          fileIds,
        });
        const response = await fetch(result.fileUrl);
        const blob = await response.blob();
        triggerBlobDownload(blob, result.fileName || "download");
        return;
      }

      // Must use axios directly with responseType "blob": the shared
      // apiPost/http client has no binary responseType, so a zip stream
      // would get decoded as text and corrupted before it reaches here.
      const response = await axios.post(
        `${BASE_URL}/v1/photos/downloadcatalogzip`,
        { fileIds, zipName },
        {
          responseType: "blob",
          headers: {
            api_key: ACCESS_TOKEN,
            Authorization: `Bearer ${readFromLocalStorage("userAccessToken") || ""}`,
          },
        },
      );

      triggerBlobDownload(
        new Blob([response.data], { type: "application/zip" }),
        `${zipName}.zip`,
      );
    } catch (error) {
      console.error("Zip download failed:", error);
      showAlert({
        title: "Error",
        text: await extractZipErrorMessage(error),
        icon: "error",
      });
    } finally {
      setZipDownloadTarget(null);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < filteredFiles.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Escape") {
      setSelectedIndex(null);
    } else if (e.key === "ArrowRight") {
      handleNext();
    } else if (e.key === "ArrowLeft") {
      handlePrevious();
    }
  };

  const getCatalogsFiles = async () => {
    try {
      setLoading(true);
      const result = await apiPost(`/v1/catalog/getsinglecatalogfiles`, {
        catalogId,
        type,
      });
      setData(result);
      const temAccess = result?.permission || 0;
      setAccess(temAccess > userAccessType.read);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCatalogsFiles();
  }, []);

  const selectedFile =
    selectedIndex !== null ? filteredFiles[selectedIndex] : null;

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const isSelected = useCallback(
    (id) => selectedIds.includes(id),
    [selectedIds],
  );

  const handleDelete = async () => {
    if (!selectedIds.length) return;
    try {
      setIsDelete(true);
      const result = await apiPost(`/v1/photos/photosdelete`, {
        fileIds: selectedIds,
      });
      setSelectedIds([]);
      setOpenDeleteView(false);
      await getCatalogsFiles();
    } catch (error) {
      console.error("Delete failed", error);
      showAlert({
        title: "Error",
        text: "Failed to delete catalogs",
        icon: "error",
      });
    } finally {
      setIsDelete(false);
      setOpenDeleteView(false);
    }
  };
  const handleCatalogsDelete = async () => {
    try {
      setIsCatalogsDelete(true);
      const result = await apiPost(`/v1/catalogs/deletecatalogs`, {
        catalogIds: [catalogId],
      });
      setSelectedIds([]);
      setOpenDeleteView(false);
      navigate(-1);
    } catch (error) {
      console.error("Delete failed", error);
      showAlert({
        title: "Error",
        text: "Failed to delete catalogs",
        icon: "error",
      });
    } finally {
      setIsCatalogsDelete(false);
      setOpenDeleteView(false);
    }
  };

  const handleDeleteOpenModal = (record) => {
    setOpenDeleteView(true);
    setCatalogs(false);
  };
  const handleDeleteCatalogsOpenModal = (record) => {
    setOpenDeleteView(true);
    setCatalogs(true);
  };
  const handleAddExistingFiles = () => {
    console.log("Multiple upload clicked");
    navigate(
      `/photos/multiupload?projectId=${projectId}&folderId=${folderId}&type=${type}&isbulk=false&catalogId=${data?.catalogId}`,
    );
  };
  const handleMultipleBulkUpload = () => {
    console.log("Multiple upload clicked");
    navigate(
      `/photos/multiupload?projectId=${projectId}&folderId=${folderId}&type=${type}&isbulk=true&catalogId=${data?.catalogId}`,
    );
  };

  const onChange = (value, name, rowId) => {
    try {
      const result = apiPost(`/v1/catalog/updatecatalog`, {
        catalogId: data?.catalogId,
        [name]: value,
      });
    } catch (e) {
      showAlert({
        title: "Error",
        text: e?.data?.message || "Failed to update catalog",
        icon: "error",
      });
    } finally {
    }
  };
  const onChangeFileName = useCallback((value, name, rowId) => {
    try {
      apiPost(`/v1/photos/updatephotos`, {
        fileId: rowId,
        [name]: value,
      });
    } catch (e) {
      console.log("Error", e);
      showAlert({
        title: "Error",
        text: e?.data?.message || "Failed to update file",
        icon: "error",
      });
    } finally {
      setData((prev) => updateFileById(prev, rowId, { fileName: value }));
    }
  }, []);

  const handleOpenFile = useCallback(
    (file) => {
      setSelectedIndex(filteredFiles.findIndex((f) => f.id === file.id));
    },
    [filteredFiles],
  );

  return (
    <>
      <CustomBreadcrumb />
      {loading ? (
        <div style={{ marginTop: "70px " }}>
          <ShimmerCard />
        </div>
      ) : data?.permission > 0 ? (
        <>
          <SC.BodyContainer>
            <SC.FilterContainer>
              <OccupantFilterDropdown />
            </SC.FilterContainer>
          </SC.BodyContainer>

          <SC.Container>
            <SC.Header>
              <SC.HeaderTop>
                <SC.HeaderLeft>
                  <InnoIcons.ArrowBackIcon
                    size={20}
                    style={{ cursor: "pointer", marginRight: "10px" }}
                    onClick={() => navigate(-1)}
                  />
                  {/* <SC.Title>{data.catalogName}</SC.Title> */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "50px",
                      flex: 1,
                    }}
                  >
                    <TextInput
                      defaultValue={data?.catalogName || ""}
                      name="name"
                      style={{
                        height: "30px",
                        fontSize: "20px",
                        // width: "700px",
                        fontWeight: "bold",
                        marginRight: "auto",
                      }}
                      disabled={!access}
                      onChange={(value) => onChange(value, "catalogName")}
                      variant="borderless"
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      // isTextArea
                    />
                  </div>
                </SC.HeaderLeft>
                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    placeholder="Search files by name..."
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSelectedIndex(null);
                      setSelectedIds([]);
                    }}
                  />
                </SC.InputContiner>
              </SC.HeaderTop>

              <SC.ActionsRow>
                <Button
                  caption={
                    zipDownloadTarget === "selected" ? (
                      <GeneratingText label="Downloading" />
                    ) : (
                      `Download Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                    )
                  }
                  color="white"
                  fontSize="12px"
                  leftIcon={
                    zipDownloadTarget === "selected" ? (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        sx={{ color: theme.colors.white }}
                      />
                    ) : (
                      <InnoIcons.DownloadIcon
                        size={16}
                        sx={{ fontSize: "17px" }}
                      />
                    )
                  }
                  onClick={() =>
                    downloadFilesAsZip(
                      selectedIds,
                      `${data.catalogName || "selected"}_files`,
                      "selected",
                    )
                  }
                  disabled={selectedIds.length === 0 || !!zipDownloadTarget}
                  style={{
                    background: theme.gradient.themeGradient,
                    color: theme.colors.white,
                    minWidth: "90px",
                    height: "34px",
                    borderRadius: "3px",
                    opacity: selectedIds.length === 0 ? 0.6 : 1,
                  }}
                />
                <Button
                  caption={
                    zipDownloadTarget === "catalog" ? (
                      <GeneratingText label="Downloading" />
                    ) : (
                      "Download Catalog"
                    )
                  }
                  color="white"
                  fontSize="12px"
                  leftIcon={
                    zipDownloadTarget === "catalog" ? (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        sx={{ color: theme.colors.white }}
                      />
                    ) : (
                      <InnoIcons.DownloadIcon
                        size={16}
                        sx={{ fontSize: "17px" }}
                      />
                    )
                  }
                  onClick={() =>
                    downloadFilesAsZip(
                      data.files.map((f) => f.id),
                      data.catalogName || "catalog",
                      "catalog",
                    )
                  }
                  disabled={data.files.length === 0 || !!zipDownloadTarget}
                  style={{
                    background: theme.gradient.themeGradient,
                    color: theme.colors.white,
                    minWidth: "90px",
                    height: "34px",
                    borderRadius: "3px",
                    opacity: data.files.length === 0 ? 0.6 : 1,
                  }}
                />

                {access && (
                  <>
                    <Button
                      caption={"Upload Photos & Videos"}
                      color="white"
                      fontSize="12px"
                      leftIcon={
                        <InnoIcons.AddIcon
                          size={16}
                          sx={{ fontSize: "17px" }}
                        />
                      }
                      onClick={handleAddExistingFiles}
                      style={{
                        background: theme.gradient.themeGradient,
                        color: theme.colors.white,
                        height: "34px",
                        borderRadius: "3px",
                      }}
                    />
                    <Button
                      caption={"Bulk Upload Photos & Videos"}
                      color="white"
                      fontSize="12px"
                      leftIcon={
                        <InnoIcons.AddIcon
                          size={16}
                          sx={{ fontSize: "17px" }}
                        />
                      }
                      onClick={handleMultipleBulkUpload}
                      maxLength={50}
                      style={{
                        background: theme.gradient.themeGradient,
                        color: theme.colors.white,
                        height: "34px",
                        borderRadius: "3px",
                      }}
                    />
                    <Button
                      caption={
                        isdelete ? (
                          <GeneratingText label="Deleting" />
                        ) : selectedIds.length > 0 ? (
                          `Delete files (${selectedIds.length})`
                        ) : (
                          "Delete files"
                        )
                      }
                      color={
                        selectedIds.length === 0 || data.permission < 2
                          ? "#9e9e9e"
                          : "white"
                      }
                      fontSize="12px"
                      leftIcon={
                        isdelete ? (
                          <CircularProgress
                            size={14}
                            thickness={5}
                            sx={{ color: theme.colors.white }}
                          />
                        ) : (
                          <InnoIcons.DeleteIcon
                            size={16}
                            sx={{ fontSize: "17px" }}
                          />
                        )
                      }
                      onClick={handleDeleteOpenModal}
                      style={{
                        background:
                          selectedIds.length === 0 || data.permission < 2
                            ? "#e0e0e0" // disabled background
                            : theme.gradient.themeGradient,
                        color:
                          selectedIds.length === 0 || data.permission < 2
                            ? "#9e9e9e"
                            : theme.colors.white,
                        minWidth: "90px",
                        height: "34px",
                        borderRadius: "3px",
                      }}
                      disabled={
                        selectedIds.length === 0 ||
                        data.permission < 2 ||
                        isdelete
                      }
                    />
                    <Button
                      caption={
                        isCatalogsDelete ? (
                          <GeneratingText label="Deleting" />
                        ) : (
                          "Delete Catalogs"
                        )
                      }
                      color="white"
                      fontSize="12px"
                      leftIcon={
                        isCatalogsDelete ? (
                          <CircularProgress
                            size={14}
                            thickness={5}
                            sx={{ color: theme.colors.white }}
                          />
                        ) : (
                          <InnoIcons.DeleteIcon
                            size={16}
                            sx={{ fontSize: "17px" }}
                          />
                        )
                      }
                      onClick={handleDeleteCatalogsOpenModal}
                      style={{
                        background: theme.gradient.themeGradient,
                        color: theme.colors.white,
                        minWidth: "90px",
                        height: "34px",
                        borderRadius: "3px",
                      }}
                      disabled={data.permission < 2}
                    />
                  </>
                )}
              </SC.ActionsRow>

              <SC.Metadata>
                <SC.MetaItem>
                  <strong>Project:</strong> {data.projectName}
                </SC.MetaItem>
                {data.projectType == projectType.occupant && (
                  <SC.MetaItem>
                    <strong>Occupant:</strong> {data.occupantName}
                  </SC.MetaItem>
                )}
                <SC.MetaItem>
                  <InnoIcons.ImageIcon size={16} />
                  {data.photoCount} Photos
                </SC.MetaItem>
                <SC.MetaItem>
                  <InnoIcons.VideocamIcon size={16} />
                  {data.videoCount} Videos
                </SC.MetaItem>

                <SC.ResultsCount>
                  {filteredFiles.length} of {data.files.length} files
                </SC.ResultsCount>
              </SC.Metadata>
            </SC.Header>

            <SC.Grid>
              {filteredFiles.length > 0 ? (
                filteredFiles.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    selected={isSelected(file.id)}
                    access={access}
                    onToggleSelect={toggleSelect}
                    onOpen={handleOpenFile}
                    onRenameFile={onChangeFileName}
                  />
                ))
              ) : (
                <SC.EmptyState>
                  <SC.EmptyIcon>
                    <InnoIcons.SearchIcon size={48} color="#ddd" />
                  </SC.EmptyIcon>
                  <SC.EmptyText>No files found</SC.EmptyText>
                </SC.EmptyState>
              )}
            </SC.Grid>

            {selectedFile && (
              <SC.Modal
                onClick={() => setSelectedIndex(null)}
                onKeyDown={handleKeyPress}
              >
                <SC.ModalContent onClick={(e) => e.stopPropagation()}>
                  <SC.ModalHeader>
                    <SC.ModalTitle>{selectedFile.fileName}</SC.ModalTitle>
                    <SC.ModalActions>
                      <Button
                        caption={
                          downloading && downloadingFileId === selectedFile.id
                            ? "Downloading..."
                            : "Download"
                        }
                        color="white"
                        fontSize="12px"
                        leftIcon={
                          <InnoIcons.DownloadIcon
                            size={16}
                            sx={{ fontSize: "17px" }}
                          />
                        }
                        onClick={() => handleDownload(selectedFile)}
                        style={{
                          background: theme.gradient.themeGradient,
                          color: theme.colors.white,
                          minWidth: "90px",
                          height: "30px",
                          borderRadius: "3px",
                        }}
                        disabled={downloading}
                      />
                      <SC.IconButton onClick={() => setSelectedIndex(null)}>
                        <InnoIcons.CloseIcon size={20} />
                      </SC.IconButton>
                    </SC.ModalActions>
                  </SC.ModalHeader>

                  <SC.MediaPreview>
                    {selectedFile.media_type === "image" ? (
                      <SC.PreviewImage
                        src={selectedFile.publicUrl}
                        alt={selectedFile.fileName}
                      />
                    ) : (
                      <SC.PreviewVideo controls>
                        <source src={selectedFile.publicUrl} />
                        Your browser does not support the video tag.
                      </SC.PreviewVideo>
                    )}
                  </SC.MediaPreview>

                  {/* Download progress bar (visible only for the currently downloading file) */}
                  {downloading && downloadingFileId === selectedFile.id && (
                    <div
                      style={{ width: "100%", marginTop: 12, padding: "20px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 6,
                        }}
                      >
                        <div style={{ fontSize: 12, color: "#666" }}>
                          Downloading...{" "}
                          {downloadProgress ? `${downloadProgress}%` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          {downloadProgress ? `${downloadProgress}%` : ""}
                        </div>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: 8,
                          background: "#eee",
                          borderRadius: 4,
                        }}
                      >
                        <div
                          style={{
                            width: `${downloadProgress}%`,
                            height: "100%",
                            borderRadius: 4,
                            background: theme.gradient.themeGradient,
                            transition: "width 120ms linear",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <SC.Navigation>
                    <SC.NavButton
                      onClick={handlePrevious}
                      disabled={selectedIndex === 0}
                    >
                      <InnoIcons.LeftArrow size={18} />
                      Previous
                    </SC.NavButton>
                    <SC.Counter>
                      {selectedIndex + 1} / {filteredFiles.length}
                    </SC.Counter>
                    <SC.NavButton
                      onClick={handleNext}
                      disabled={selectedIndex === filteredFiles.length - 1}
                    >
                      Next
                      <InnoIcons.RightArrow size={18} />
                    </SC.NavButton>
                  </SC.Navigation>
                </SC.ModalContent>
              </SC.Modal>
            )}
          </SC.Container>
        </>
      ) : (
        <>
          <AccessDeniedMessage />
        </>
      )}

      {openDeleteView && (
        <DeleteComponent
          title={catalogs ? "Delete Catalogs" : "Delete Files"}
          messageData={
            catalogs
              ? `Are you sure you want to delete catalog?`
              : `Are you sure you want to delete (${selectedIds.length}) file(s)?`
          }
          setOpenDeleteView={setOpenDeleteView}
          openDeleteView={openDeleteView}
          handleDelete={catalogs ? handleCatalogsDelete : handleDelete}
          firstButtontext={"Cancel"}
        />
      )}
    </>
  );
};

export default CatalogDetails;
